import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { combineLatest, filter, firstValueFrom, take } from 'rxjs';
import {
    loadStripe,
    Stripe,
    StripeAddressElement,
    StripeElements,
    StripeLinkAuthenticationElement,
    StripePaymentElement,
} from '@stripe/stripe-js';
import { environment } from 'projects/angular-clarity/src/environments/environment';
import { selectMspData } from 'projects/angular-clarity/src/app/modules/msp/store';
import { sendMspLockoutEmail } from 'projects/angular-clarity/src/app/modules/msp/store/msp/actions';
import * as fromRoot from 'projects/angular-clarity/src/app/stores/root.store';
import { deleteSession } from 'projects/angular-clarity/src/app/stores/root/octiga/session/actions';
import { MspAjaxService } from 'projects/angular-clarity/src/app/services/ajax/msp-ajax.service';
import { Countries } from 'projects/angular-clarity/src/app/utils/ISO-3166-Countries';
import { STRIPE_APPEARANCE, STRIPE_FONT_SRC } from 'projects/angular-clarity/src/app/utils/stripe-appearance';

/** Response from `/billing/checkout`: the SetupIntent client secret used to confirm the card setup. */
interface CheckoutResponse {
    clientSecret: string;
}

/** One volume tier of a per-mailbox price, as returned by the billing pricing endpoint. */
interface PricingTier {
    /** Inclusive lower bound of the mailbox range. */
    minUnits: number;
    /** Inclusive upper bound, or `null` for the unbounded top tier. */
    maxUnits: number | null;
    /** Per-mailbox monthly price as a decimal string, e.g. `"1.50"`. */
    unitPrice: string;
}

/** Pricing for the currency derived from the customer's country. */
interface PricingResponse {
    plan: string;
    currency: string;
    tiers: PricingTier[];
}

/** A distributor's customer-facing contact (who an MSP should reach out to for onboarding). */
interface DistributorContact {
    name: string;
    email: string;
    note: string | null;
}

/** The distributor governing an MSP's country, with the contacts to reach out to for onboarding. */
interface DistributorInfo {
    name: string;
    contacts: DistributorContact[];
}

/** Response from `/billing/distributor`: the governing distributor, or null when the country is direct. */
interface DistributorResponse {
    distributor: DistributorInfo | null;
}

/** Currency code → display symbol; falls back to the code itself when unknown. */
const CURRENCY_SYMBOLS: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    AUD: 'A$',
};

/**
 * Trial-expired self-service signup: a two-step modal. **Step 1** lets the customer pick a plan and
 * shows the resulting mailbox pricing table; **step 2** collects our own fields (company, VAT)
 * alongside native Stripe Elements (email, billing name + address, card), saves a card via a
 * SetupIntent (no charge), creates the billing record and unlocks the MSP.
 *
 * Country is collected once — by the Stripe `address` element (step 2). It's seeded from the MSP's
 * stored `country_code`, and its value (currency-derived server-side) drives the mailbox pricing list
 * shown on step 1 — so pricing is available before the address element exists.
 *
 * The Stripe Elements run in **deferred** `mode: 'setup'`, so they need no client secret to mount.
 * They are created up-front (while the customer is on step 1) but only mounted once step 2 is first
 * revealed (see {@link elementsRevealed} / {@link next}), so they measure their width at a settled,
 * visible size. The SetupIntent is created at submit time (`POST /billing/checkout`) using the final
 * field values — including the billing name read back from the native address element — and confirmed
 * in place with no redirect (`stripe.confirmSetup({ redirect: 'if_required' })`), so the modal never
 * navigates away. A separate `activation_lockout` branch (unvalidated MSPs) short-circuits the whole
 * flow and just asks them to email support.
 *
 * A third branch covers **distributor-governed countries**: when the MSP's country maps to a
 * distributor (`GET /billing/distributor`), the modal shows a "contact your distributor" message
 * (distributor name + contacts) instead of any pricing/card flow, and Stripe is never loaded. The path
 * is resolved on init — see {@link resolveOnboardingPath} — and gated behind {@link resolving} so the
 * self-service form never flashes before the distributor branch is known.
 */
@Component({
    selector: 'app-trial-expire-modal',
    templateUrl: './trial-expire-modal.component.html',
    styleUrls: ['./trial-expire-modal.component.scss'],
})
export class TrialExpireDialogComponent implements OnInit, OnDestroy {
    public activation_lockout;
    public user = '';
    public submitting = false;
    public errorMessage = '';
    /** Set once signup completes — swaps the form for the inline success message. */
    public success = false;

    /**
     * True while we resolve which onboarding path applies (distributor vs self-service) on init. Holds
     * the whole content so the self-service form never flashes before {@link distributor} is known.
     * Does not apply to the `activation_lockout` branch, which is known synchronously from the MSP.
     */
    public resolving = true;

    /**
     * Set when the MSP's country is governed by a distributor: the modal then shows a "contact your
     * distributor" message (no pricing table, no card) instead of the self-service flow, and Stripe is
     * never loaded. Null = direct (self-service) signup.
     */
    public distributor: DistributorInfo | null = null;

    /**
     * Display name of the MSP's country (e.g. "Germany"), resolved from its ISO alpha-2 code for the
     * distributor branch's copy. Falls back to the raw code, then to "your region" when unknown.
     */
    public countryName = 'your region';

    /** Email domain of the first distributor contact (e.g. "somedisty.com"), used in the dialog copy. */
    public get distributorDomain(): string | null {
        const email = this.distributor?.contacts?.[0]?.email;
        if (!email) return null;
        const at = email.indexOf('@');
        return at >= 0 ? email.slice(at + 1) : null;
    }

    /** Which step of the two-step modal is visible: `1` = choose plan/pricing, `2` = billing details. */
    public step: 1 | 2 = 1;

    /** Pricing tiers for the currently-selected country's currency (loaded from the API). */
    public pricingTiers: PricingTier[] = [];
    /** ISO currency code the tiers are priced in (drives the displayed symbol/label). */
    public pricingCurrency: string | null = null;
    public pricingLoading = false;
    /** True when the pricing lookup failed (network/server error). */
    public pricingError = false;

    /**
     * Our own (non-Stripe) fields. The billing email, name, address, country, and tax ID are NOT
     * here — the native Stripe Elements collect them, and `signup` persists them onto the Stripe
     * customer. The one exception is `usTaxId`: Stripe's Tax ID Element doesn't collect a tax ID for
     * the US at all, so a plain field takes over for that one country — see {@link isUsCountry}.
     */
    public readonly signupForm = this.fb.group({
        plan: ['FLEX', [Validators.required]],
        minCommit: [null as number | null],
        usTaxId: [null as string | null],
        /** Optional — falls back to the address element's billing-contact name if left blank. */
        usBusinessName: [null as string | null],
    });

    /** ISO alpha-2 country from the MSP record, used to seed the Stripe address element. */
    private mspCountryCode: string | null = null;
    /** Current billing country (seeded from the MSP, then owned by the address element) — drives pricing. */
    private selectedCountry: string | null = null;
    /**
     * True when the selected billing country is the US. Stripe's Tax ID Element has no supported tax
     * ID type for the US — it just hides the type/value fields there — so `usTaxId` on
     * {@link signupForm} collects the EIN instead, bypassing the Stripe element for that field.
     */
    public isUsCountry = false;

    /** Loaded Stripe.js instance (publishable key); null until {@link initStripe} resolves. */
    private stripe: Stripe | null = null;
    /** The deferred-mode Elements group the three native elements belong to. */
    private elements?: StripeElements;
    private emailElement?: StripeLinkAuthenticationElement;
    private addressElement?: StripeAddressElement;
    private paymentElement?: StripePaymentElement;
    private taxIdElement?: any;
    /** Guards against mounting the elements twice (Stripe throws on a double mount). */
    public elementsMounted = false;
    /**
     * Set when the customer first advances to step 2. The elements are created on step 1 but only
     * mounted once this is true, so they measure their width while actually visible (see {@link next}).
     */
    private elementsRevealed = false;

    /** Per-element completeness, tracked from each element's `change` event to gate the submit button. */
    public emailComplete = false;
    public addressComplete = false;
    public cardComplete = false;
    public taxIdComplete = false;
    public taxIdEmpty = true;
    /** Last tax ID captured from the taxId element's change event (the beta element's getValue() is unreliable). */
    private taxIdValue: { type: string; value: string } | null = null;
    /** Business name captured from the taxId element's change event. */
    private taxIdBusinessName = '';
    /** Billing email captured from the `linkAuthentication` element (it has no `getValue()`). */
    private billingEmail = '';

    // The element containers only render once we know the MSP isn't in activation lockout, so guard the
    // mount on both the (async-loaded) Stripe Elements and the container refs via these setters.
    @ViewChild('emailEl') set emailEl(ref: ElementRef<HTMLElement> | undefined) {
        this.emailContainer = ref?.nativeElement;
        this.tryMountElements();
    }
    @ViewChild('addressEl') set addressEl(ref: ElementRef<HTMLElement> | undefined) {
        this.addressContainer = ref?.nativeElement;
        this.tryMountElements();
    }
    @ViewChild('cardEl') set cardEl(ref: ElementRef<HTMLElement> | undefined) {
        this.cardContainer = ref?.nativeElement;
        this.tryMountElements();
    }
    @ViewChild('taxIdEl') set taxIdEl(ref: ElementRef<HTMLElement> | undefined) {
        this.taxIdContainer = ref?.nativeElement;
        this.tryMountElements();
    }
    private emailContainer?: HTMLElement;
    private addressContainer?: HTMLElement;
    private cardContainer?: HTMLElement;
    private taxIdContainer?: HTMLElement;

    private mspId = '';

    private $session = this.store.pipe(
        select(fromRoot.selectSession),
        filter((state) => state.isAuthenticated && !!state.session.msp_id),
    );

    private $msp = this.store.pipe(
        select(selectMspData),
        filter((msp) => !!msp),
    );

    constructor(
        public store: Store<any>,
        public modal: MatDialogRef<TrialExpireDialogComponent>,
        private fb: FormBuilder,
        private mspAjax: MspAjaxService,
    ) {}

    ngOnInit(): void {
        // Reload the pricing table and update the minCommit validator whenever the plan changes.
        this.signupForm.get('plan')!.valueChanges.subscribe((plan) => {
            const minCommitControl = this.signupForm.get('minCommit')!;
            if (plan === 'COMMIT') {
                minCommitControl.setValidators([Validators.required, Validators.min(1)]);
            } else {
                minCommitControl.clearValidators();
                minCommitControl.reset(null);
            }
            minCommitControl.updateValueAndValidity();
            this.loadPricing();
        });

        // Once the dialog's open animation finishes the containers are at their final width — re-measure
        // the Stripe elements then too, in case they mounted (and collapsed) mid-animation. See
        // nudgeElementsLayout / tryMountElements.
        this.modal
            .afterOpened()
            .pipe(take(1))
            .subscribe(() => this.nudgeElementsLayout());

        combineLatest([this.$session, this.$msp])
            .pipe(take(1))
            .subscribe(([session, msp]) => {
                this.user = session.session.upn;
                this.mspId = msp.id;
                this.store.dispatch(
                    sendMspLockoutEmail({
                        msp_id: msp.id,
                        name: msp.name,
                        upn: this.user,
                        country_code: msp.country_code,
                    }),
                );
                this.activation_lockout = msp.activation_lockout;

                // Seed the country from the MSP record: it pre-fills the Stripe address element and
                // drives the initial pricing lookup before the elements have mounted.
                this.mspCountryCode = msp.country_code ?? null;
                this.setCountry(this.mspCountryCode);

                // Lockout MSPs see the "email support" branch — known synchronously, no path to resolve.
                if (this.activation_lockout) {
                    this.resolving = false;
                    return;
                }

                // Otherwise resolve distributor-governed vs self-service before showing any content.
                this.resolveOnboardingPath();
            });
    }

    /**
     * Update the current billing country and, for the US special-case, the `usTaxId` control's
     * validators. Stripe's Tax ID Element has no supported tax ID type for the US — it hides the
     * type/value fields there and shows only business name — so `usTaxId` is required in its place
     * only while the US is selected, and cleared otherwise so it can't block submission for other
     * countries. Also toggles the Stripe element's own `visibility` so it's fully hidden (not just its
     * tax-ID sub-fields) whenever the US field is shown instead — never both at once.
     */
    private setCountry(country: string | null): void {
        this.selectedCountry = country;
        this.isUsCountry = country === 'US';

        const usTaxIdControl = this.signupForm.get('usTaxId')!;
        usTaxIdControl.setValidators(this.isUsCountry ? [Validators.required] : []);
        usTaxIdControl.updateValueAndValidity();

        this.taxIdElement?.update({ visibility: this.isUsCountry ? 'never' : 'always' });
    }

    /**
     * Decide which onboarding path the (validated) MSP gets: distributor-governed or self-service.
     * Looks up the MSP's country against the distributor mapping; a hit shows the "contact your
     * distributor" branch (no pricing/card, Stripe never loaded), otherwise the self-service flow
     * starts. A missing country or a lookup error falls back to self-service so the MSP is never
     * blocked from signing up. Clears {@link resolving} once the branch is known.
     */
    private resolveOnboardingPath(): void {
        const country = this.mspCountryCode;
        if (!country) {
            this.startSelfServiceSignup();
            return;
        }

        // Resolve the ISO alpha-2 code to a country name for the distributor branch's copy.
        this.countryName = Countries.find((c) => c['alpha-2'] === country)?.name ?? country;

        this.mspAjax
            .get<DistributorResponse>(`/api/octiga/v3/msp/billing/distributor?country=${encodeURIComponent(country)}`)
            .pipe(take(1))
            .subscribe({
                next: (res) => {
                    if (res.distributor) {
                        this.distributor = res.distributor;
                        this.resolving = false;
                    } else {
                        this.startSelfServiceSignup();
                    }
                },
                error: () => this.startSelfServiceSignup(),
            });
    }

    /** Reveal the self-service signup flow: load the pricing table and the Stripe payment form. */
    private startSelfServiceSignup(): void {
        this.resolving = false;
        this.loadPricing();
        void this.initStripe();
    }

    /** Fetch the pricing tiers for the selected plan and the currency derived from the country. */
    private loadPricing(): void {
        const plan = this.signupForm.get('plan')!.value;
        const country = this.selectedCountry;

        if (!plan || !country) {
            this.pricingTiers = [];
            this.pricingCurrency = null;
            return;
        }

        this.pricingLoading = true;
        this.pricingError = false;

        this.mspAjax
            .get<PricingResponse>(
                `/api/octiga/v3/msp/billing/pricing?plan=${encodeURIComponent(plan)}&country=${encodeURIComponent(country)}`,
            )
            .pipe(take(1))
            .subscribe({
                next: (res) => {
                    this.pricingLoading = false;
                    this.pricingCurrency = res.currency;
                    this.pricingTiers = res.tiers;
                    // Keep the Elements' currency in step with the resolved currency (influences the
                    // payment methods Stripe offers). Card is currency-agnostic, so this is best-effort.
                    this.elements?.update({ currency: res.currency.toLowerCase() });
                },
                error: () => {
                    this.pricingLoading = false;
                    this.pricingError = true;
                    this.pricingTiers = [];
                    this.pricingCurrency = null;
                },
            });
    }

    /** Human-readable mailbox range for a tier, e.g. `"101 – 200"` or `"5001 +"`. */
    public tierRange(tier: PricingTier): string {
        return tier.maxUnits === null ? `${tier.minUnits} +` : `${tier.minUnits} – ${tier.maxUnits}`;
    }

    /** Per-mailbox price with the currency symbol, e.g. `"€1.40"`. */
    public tierPrice(tier: PricingTier): string {
        const symbol = this.pricingCurrency ? (CURRENCY_SYMBOLS[this.pricingCurrency] ?? '') : '';
        return `${symbol}${tier.unitPrice}`;
    }

    /** Footnote describing the selected plan's billing terms. */
    public planNote(): string {
        return this.signupForm.get('plan')?.value === 'COMMIT'
            ? '12-month commitment, billed monthly.'
            : 'Billed monthly, no commitment.';
    }

    /**
     * Advance from the plan/pricing step to the billing-details step. The first time it's reached this
     * reveals and mounts the Stripe elements (deferred from step 1 so they size correctly while
     * visible); on subsequent visits it just re-nudges their layout after the step is shown again.
     */
    public next(): void {
        this.step = 2;
        this.elementsRevealed = true;
        // Defer past this change-detection pass so the step-2 containers are visible (un-hidden)
        // before Stripe measures them; then mount (first visit) and re-nudge the layout.
        setTimeout(() => {
            this.tryMountElements();
            this.nudgeElementsLayout();
        });
    }

    /** Return from the billing-details step to the plan/pricing step. */
    public back(): void {
        this.step = 1;
    }

    /**
     * Load Stripe.js and create the deferred-mode Elements group + the three native elements (email,
     * billing address, card). Deferred `mode: 'setup'` means no client secret is needed to mount — the
     * SetupIntent is created later, at submit. Mounting happens in {@link tryMountElements} once the
     * containers render.
     */
    private async initStripe(): Promise<void> {
        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.stripe = await loadStripe(environment.stripePublishableKey, { betas: ['elements_tax_id_1'] } as any);
            if (!this.stripe) throw new Error('Stripe failed to load');

            this.elements = this.stripe.elements({
                mode: 'setup',
                currency: this.pricingCurrency?.toLowerCase() ?? 'usd',
                setupFutureUsage: 'off_session',
                // Card only — declaring paymentMethodTypes opts out of automatic payment methods, which
                // would otherwise surface Revolut Pay / Bancontact etc. for the resolved currency.
                paymentMethodTypes: ['card'],
                // Style the native elements through Stripe's Appearance API (keyed to the Octiga brand)
                // and supply Montserrat so the iframe matches the host page's type.
                appearance: STRIPE_APPEARANCE,
                fonts: [{ cssSrc: STRIPE_FONT_SRC }],
            });

            this.emailElement = this.elements.create('linkAuthentication');
            this.emailElement.on('change', (e) => {
                this.billingEmail = e.value.email;
                this.emailComplete = e.complete;
            });

            // Seed the address element's country from the MSP record so the customer doesn't re-pick
            // it; it's the single source of truth for country thereafter.
            this.addressElement = this.elements.create('address', {
                mode: 'billing',
                defaultValues: this.mspCountryCode ? { address: { country: this.mspCountryCode } } : undefined,
            });
            this.addressElement.on('change', (e) => {
                this.addressComplete = e.complete;
                // Country drives the currency/pricing — reload the tiers whenever it changes.
                const country = e.value.address.country;
                if (country && country !== this.selectedCountry) {
                    this.setCountry(country);
                    this.loadPricing();
                }
            });

            // Link (the "Save my information for faster checkout" prompt) is already excluded by the
            // Elements group's `paymentMethodTypes: ['card']` above, so no per-element wallet config is needed.
            this.paymentElement = this.elements.create('payment');
            this.paymentElement.on('change', (e) => (this.cardComplete = e.complete));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.taxIdElement = (this.elements as any).create('taxId', {
                // Fully hidden for the US from the start if that's already the seeded country — the
                // custom `usTaxId` field takes over there instead. Kept in sync by `setCountry`.
                visibility: this.isUsCountry ? 'never' : 'always',
                validation: {
                    businessName: { required: 'always' },
                    // 'always', not 'auto' — 'auto' turned out to make the tax ID optional for every
                    // country, not just the US (confirmed: EU VAT could be skipped entirely). The US
                    // bypass is handled solely by the `visibility: 'never'` toggle above/in
                    // `setCountry` — this element is never even rendered for the US, so `required`
                    // here has no effect there.
                    taxId: { required: 'always' },
                },
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.taxIdElement.on('change', (e: any) => {
                this.taxIdComplete = e.complete;
                this.taxIdEmpty = e.empty;
                const taxIdVal = e.value?.taxId;
                const taxIdType = e.value?.externalTaxIdType;
                this.taxIdValue = taxIdVal && taxIdType ? { type: taxIdType, value: taxIdVal } : null;
                this.taxIdBusinessName = e.value?.businessName ?? '';
            });

            this.tryMountElements();
        } catch {
            this.errorMessage = 'We could not load the payment form. Please refresh or contact support@octiga.com.';
        }
    }

    /**
     * Mount the three elements once they're revealed (step 2) and both their containers and the
     * elements exist. Mounting is deferred until step 2 is shown so the elements measure their width
     * while visible rather than collapsed behind the hidden first step.
     */
    private tryMountElements(): void {
        if (
            !this.elementsRevealed ||
            this.elementsMounted ||
            !this.emailElement ||
            !this.addressElement ||
            !this.paymentElement ||
            !this.taxIdElement ||
            !this.emailContainer ||
            !this.addressContainer ||
            !this.cardContainer ||
            !this.taxIdContainer
        ) {
            return;
        }
        this.elementsMounted = true;
        this.emailElement.mount(this.emailContainer);
        this.addressElement.mount(this.addressContainer);
        this.taxIdElement.mount(this.taxIdContainer);
        this.paymentElement.mount(this.cardContainer);

        // Stripe's Appearance-API layout measures each element's width once at mount and then only
        // re-measures on a real container resize. When the elements mount while the MatDialog is still
        // running its open animation, that first measurement is taken at a transient width and the
        // multi-field address/card elements collapse to ~0 width (their labels wrap one char per line).
        // A plain `window` resize doesn't fix the multi-field elements — only an actual change to the
        // container box does — so nudge each container's width a few times after mount to force Stripe's
        // in-iframe ResizeObserver to re-measure at the settled width. (Verified in Stripe test mode.)
        [150, 400, 800].forEach((ms) => setTimeout(() => this.nudgeElementsLayout(), ms));
    }

    /**
     * Force a sub-pixel width reflow on the three Stripe element containers so Stripe re-measures and
     * expands any element that collapsed when first measured mid-dialog-animation. Idempotent and
     * harmless once the layout is already correct. See {@link tryMountElements} for the why.
     */
    private nudgeElementsLayout(): void {
        for (const el of [this.emailContainer, this.addressContainer, this.taxIdContainer, this.cardContainer]) {
            if (!el) continue;
            el.style.width = '99.99%';
            void el.offsetWidth; // force a synchronous reflow between the two writes
            el.style.width = '';
        }
    }

    /** Select a plan card on step 1 (mirrors the previous `<select>`'s value-change behaviour). */
    public selectPlan(plan: string): void {
        this.signupForm.get('plan')!.setValue(plan);
    }

    /** True when the step-1 plan choice is complete — gates the Continue button. */
    public get canContinue(): boolean {
        const plan = this.signupForm.get('plan')!.value;
        if (plan !== 'COMMIT') return true;
        const minCommit = this.signupForm.get('minCommit')!.value;
        return Number.isInteger(minCommit) && (minCommit as number) >= 1;
    }

    /**
     * True when our fields are valid and all Stripe elements are complete — gates submit. The taxId
     * element is fully hidden (`visibility: 'never'`) for the US, so its completeness is skipped
     * there — `signupForm.valid` (which includes the required `usTaxId` control) covers it instead.
     */
    public get canSubmit(): boolean {
        return (
            this.signupForm.valid &&
            this.elementsMounted &&
            this.emailComplete &&
            this.addressComplete &&
            (this.isUsCountry || this.taxIdComplete) &&
            this.cardComplete &&
            !this.submitting
        );
    }

    /**
     * Finalize signup on a single submit: validate the elements, create the SetupIntent
     * (`/billing/checkout`) with the final field values, confirm the card setup in place (no redirect),
     * then persist the billing record / unlock the MSP (`/billing/signup`). The billing name + address
     * are read back from the native address element; the email from the link-authentication element.
     */
    public async submit(): Promise<void> {
        if (!this.canSubmit || !this.stripe || !this.elements || !this.addressElement || !this.taxIdElement) return;

        this.submitting = true;
        this.errorMessage = '';

        try {
            // Deferred flow requires submitting the Elements before confirming.
            const { error: submitError } = await this.elements.submit();
            if (submitError) {
                this.errorMessage = submitError.message ?? 'Please check your payment details and try again.';
                return;
            }

            // The native address element is the source of truth for the billing name, address, and
            // country (the latter derives the currency stamped on the customer at checkout).
            const { value: addressValue } = await this.addressElement.getValue();
            const country = addressValue.address.country;

            // Create (or re-link) the Stripe customer + SetupIntent now, stamped with the final values.
            const { plan, usTaxId, usBusinessName } = this.signupForm.getRawValue();

            // The US bypasses the Stripe taxId element entirely — it has no supported tax ID type for
            // the US, so the `usTaxId` form control (required while `isUsCountry`, see `setCountry`)
            // is the source of truth there instead. Everywhere else, `undefined` when the country has
            // no Stripe-supported tax ID type (the element hides its fields and `taxIdComplete` is
            // already true, so there's nothing to validate here).
            const taxId = this.isUsCountry
                ? { type: 'us_ein', value: (usTaxId ?? '').trim() }
                : this.taxIdValue?.type && this.taxIdValue?.value?.trim()
                  ? { type: this.taxIdValue.type, value: this.taxIdValue.value.trim() }
                  : undefined;

            // The US bypasses the Stripe taxId element's business-name field too (it's hidden along
            // with the rest of that element — see `setCountry`), so `usBusinessName` is the source of
            // truth there instead; both fall back to the billing-contact name if left blank.
            const companyName = this.isUsCountry
                ? usBusinessName?.trim() || addressValue.name
                : this.taxIdBusinessName || addressValue.name;

            const { clientSecret } = await firstValueFrom(
                this.mspAjax.post<CheckoutResponse>('/api/octiga/v3/msp/billing/checkout', {
                    plan,
                    companyName,
                    country,
                    name: addressValue.name,
                    email: this.billingEmail,
                    address: {
                        line1: addressValue.address.line1,
                        line2: addressValue.address.line2,
                        city: addressValue.address.city,
                        state: addressValue.address.state,
                        postal_code: addressValue.address.postal_code,
                        country: addressValue.address.country,
                    },
                    taxId,
                }),
            );

            // Confirm the card setup. `redirect: 'if_required'` keeps a plain card setup in place; only
            // redirect-based methods / 3DS would surface an interstitial, handled by Stripe in the modal.
            const { error, setupIntent } = await this.stripe.confirmSetup({
                elements: this.elements,
                clientSecret,
                confirmParams: { return_url: window.location.href },
                redirect: 'if_required',
            });

            if (error) {
                this.errorMessage =
                    error.message ?? 'We could not save your card. Please try again or contact support@octiga.com.';
                return;
            }
            if (setupIntent?.status !== 'succeeded') {
                this.errorMessage = 'Card setup did not complete. Please try again or contact support@octiga.com.';
                return;
            }

            // Verify the card was captured and unlock the MSP — billing details already written at checkout.
            const minCommit = plan === 'COMMIT' ? (this.signupForm.get('minCommit')!.value as number) : undefined;
            await firstValueFrom(
                this.mspAjax.post('/api/octiga/v3/msp/billing/signup', {
                    setupIntentId: setupIntent.id,
                    ...(minCommit !== undefined ? { minCommit } : {}),
                }),
            );

            this.success = true;
        } catch (err: any) {
            this.errorMessage =
                err?.message ?? 'We could not complete signup. Please try again or contact support@octiga.com.';
        } finally {
            this.submitting = false;
        }
    }

    /** Reload the app so the refreshed MSP status unlocks the product. */
    public continue(): void {
        window.location.reload();
    }

    ngOnDestroy(): void {
        this.emailElement?.destroy();
        this.addressElement?.destroy();
        this.taxIdElement?.destroy();
        this.paymentElement?.destroy();
    }

    public deleteSession(): void {
        this.store.dispatch(
            deleteSession({
                microsoftSignout: true,
            }),
        );
    }
}
